import styled from '@emotion/styled'
import React, { useEffect, useState, useRef } from 'react'
import WebComponentContainer from '../generic/WebComponentContainer'
import useEventBus from '../generic/useEventBus'
import { AftermarketPageNumber, breakpoints } from '../../constants'
import { BrowserUtils, StringUtils } from '../../utils/siteUtils'
import { QUESTION_TWO_KEYS } from '../../utils/vehicleUtils'

const LoadingWrapper = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
`

const VehicleDetailsWebComponent = ({
  dealerId,
  isDigitalRetailing,
  makeId,
  mileage,
  modelId,
  trimId,
  transmissionId,
  engineId,
  drivetrainId,
  colorId,
  updateGlobalState,
  vin,
  yearId,
  dealerName,
  isValidVehicleConfig,
  navigateWithState,
  setVehDetailsAnalytics,
  urlState,
  allowIneligibleVehiclesForWebComponent,
  location: locationWindow,
  isOfferCreationComponentEnabled,
  saveQuestionResponse
}) => {
  const TOGGLE_MODS_QUESTION_ID = 'toggleQuestionModifications1'

  const [pageLoaded, setPageLoaded] = useState(false)
  const [showLoader, setShowLoader] = useState(true)
  const [showKeysModsLoader, setShowKeysModsLoader] = useState(false)
  const [isVehicleDataValid, setIsVehicleDataValid] = useState(false)
  const [tedc, setTedc] = useState({ transmission: '', engine: '', drivetrain: '', color: '' })
  const [vehicleDetailsValid, setVehicleDetailsValid] = useState(false)
  const [isHistoryApiSuccessful, setIsHistoryApiSuccessful] = useState(true) //AutoCheckFetchSuccess eventbus is available too
  const [location, setLocation] = useState('vehicleDetails')
  const modalRef = useRef(null)
  const [isComponentModalOpen, setIsComponentModalOpen] = useState(false)
  const [modalPositionOffsetTop, setModalPositionOffsetTop] = useState('')
  const isIframe = BrowserUtils.isIframe()
  const [confirmingRemoveMods, setConfirmingRemoveMods] = useState(false)

  const locationInit = StringUtils.getUrlState(locationWindow)
  const currentPage = locationInit?.p
  const storageData = sessionStorage.getItem('ico.funnel.store.answers')

  useEffect(() => {
    setTedc({ ...tedc, transmission: transmissionId, engine: engineId, drivetrain: drivetrainId, color: colorId })
    if (currentPage < AftermarketPageNumber || !storageData) {
      setPageLoaded(true)
    }
    setVehicleDetailsValid(isValidVehicleConfig)
    if (isIframe) {
      setModalPositionOffsetTop(300)
    }
  }, [transmissionId, engineId, drivetrainId, isValidVehicleConfig])

  useEffect(() => {
    if (typeof yearId !== 'undefined' && typeof makeId !== 'undefined' && typeof modelId !== 'undefined' && typeof trimId !== 'undefined' && typeof vin !== 'undefined' && typeof mileage !== 'undefined') {
      setIsVehicleDataValid(true)
    }
  }, [yearId, makeId, modelId, trimId, vin, mileage])

  useEffect(() => {
    if (isValidVehicleConfig) {
      setShowKeysModsLoader(true)
    }
  }, [isValidVehicleConfig])

  useEffect(() => {
    if (isComponentModalOpen && isIframe) {
      modalRef.current.scrollIntoView({ block: 'start', inline: 'start', behavior: 'smooth' })
    }
  }, [isComponentModalOpen])

  useEventBus('QuestionAnswered', event => {
    const { answers, answered } = event
    const [[questionId, response]] = Object.entries(answered || { '': '' })

    if (answers?.vehicle || ['transmissionId', 'enginedId', 'drivetrainId', 'colorId'].includes(questionId)) {
      saveBasicVehicleResponse(event)
    } else if (questionId === QUESTION_TWO_KEYS) {
      saveQuestionResponse(QUESTION_TWO_KEYS, response ? 0 : 1)
    } else if (answered.section === 'conditions' && questionId.startsWith('qc/20200')) {
      saveModsResponse(event)
    }

    if (!answered) return

    const toggleMods = answered[TOGGLE_MODS_QUESTION_ID]
    if (toggleMods === true) {
      updateGlobalState({ fireAnalytics: 'ico_mods' })
    } else if (toggleMods === false && !confirmingRemoveMods) {
      setConfirmingRemoveMods(true)
    }
  })

  const saveBasicVehicleResponse = event => {
    const vehicle = event.answers?.vehicle || event.vehicle
    const { transmissionId: t, engineId: e, drivetrainId: d, colorId: c } = vehicle
    setTedc({
      ...tedc,
      transmission: t,
      engine: e,
      drivetrain: d,
      color: c
    })
    const vehicleData = { t, e, d, c }
    navigateWithState(vehicleData)
  }

  const saveModsResponse = ({ answered = {}, conditions }) => {
    const [[questionId]] = Object.entries(answered)
    const condition = conditions[questionId]
    const parentQuestionId = questionId.split('/').slice(0, -1).join('/')
    const isRadio = condition.type === 'SingleSelect'

    // For radio selections (SingleSelect), we need to find which option was selected
    // by iterating through all related questions with the same parent
    // Example: If we have questions like "qc/20200/1", "qc/20200/2", "qc/20200/3"
    // and "qc/20200/2" is selected (value=true), then we save {"qc/20200/2": 1}
    let value = undefined
    if (isRadio) {
      let i = 0
      while (true) {
        const questionId = `${parentQuestionId}/${i + 1}`
        const radio = conditions[questionId]
        if (radio?.value === true) value = i
        else if (!radio) break
        i++
      }
    } else {
      value = condition.value ? 0 : undefined
    }

    const key = isRadio ? parentQuestionId : questionId
    navigateWithState({ [key]: { v: value, c: condition.comment || undefined } })
  }

  // Post message to host page
  useEventBus('DRIneligibleUseTradeInValue', event => {
    BrowserUtils.postMessage(JSON.stringify(event), '*')
  })

  useEventBus('VehicleFetchSuccess', event => {
    setShowLoader(false)
  })

  useEventBus('IsModalOpen', event => {
    if (isIframe) {
      setIsComponentModalOpen(event)
    }
  })

  useEventBus('QuestionsInitSuccess', event => {
    setShowKeysModsLoader(false)
    setPageLoaded(true)
    if (vehicleDetailsValid) {
      setLocation('vehicleDetailsKeysMods')
    } else {
      setLocation('vehicleDetails')
    }
  })

  useEventBus('TwoKeysModsUserInteraction', event => {
    const impressions = Object.keys(event).map(userInteraction => {
      return { key: userInteraction, value: event[userInteraction] }
    })
    setVehDetailsAnalytics(impressions)

    // fields from eventBus message that can disable the yes its perfect modal
    const modificationAnsweredKeys = ['exhaust', 'performance', 'stereo', 'suspension', 'wheelstires']
    const hasInteraction = modificationAnsweredKeys.some(interaction => event[interaction] === true)
    // hasWCMods = has webcomponent modifications flag
    urlState['hasWCMods'] = hasInteraction

    if (event.mods === false && confirmingRemoveMods) {
      clearModsResponses()
    }
    setConfirmingRemoveMods(false)
  })

  const clearModsResponses = () => {
    const undefinedMods = Object.fromEntries(
      Object.entries(urlState)
        .filter(([key]) => key?.startsWith('qc/20200'))
        .map(([key]) => [key, undefined])
    )
    navigateWithState(undefinedMods)
  }

  useEventBus('HistoryApiFailure', event => {
    if (event) {
      setIsHistoryApiSuccessful(false)
    }
  })

  useEffect(() => {
    if (currentPage >= AftermarketPageNumber && storageData) {
      setPageLoaded(false)
    }
  }, [])

  const { transmission, engine, drivetrain, color } = tedc

  return (
    <div style={{ width: '100%', marginBottom: '1.5rem' }}>
      {isVehicleDataValid && (
        <WebComponentContainer pageLoadingComplete={pageLoaded} withLoader={showLoader} location={location}>
          <ico-vehicle-config-hydrator
            ref={modalRef}
            dealer-id={dealerId}
            dealer-name={dealerName}
            is-digital-retailing={isDigitalRetailing}
            make-id={makeId}
            mileage={mileage}
            model-id={modelId}
            trim-id={trimId}
            vin={vin}
            year-id={yearId}
            color-id={colorId}
            allow-ineligible-vehicle-submission={allowIneligibleVehiclesForWebComponent}>
            <ico-vehicle-config />
          </ico-vehicle-config-hydrator>
          {showKeysModsLoader && vehicleDetailsValid ? (
            <LoadingWrapper>
              <ico-loading-spinner />
            </LoadingWrapper>
          ) : (
            ''
          )}

          {vehicleDetailsValid && (
            <KeysModWrapper>
              <keys-modifications-state-hydrator
                make-id={makeId}
                mileage={mileage}
                model-id={modelId}
                trim-id={trimId}
                vin={vin}
                year-id={yearId}
                color-id={color}
                transmission-id={transmission}
                engine-id={engine}
                drivetrain-id={drivetrain}
                show-two-keys-question="true"
                modal-position-offset-top={modalPositionOffsetTop}
                allow-ineligible-vehicle-submission={allowIneligibleVehiclesForWebComponent}>
                <vehicle-keys-modifications />
              </keys-modifications-state-hydrator>
            </KeysModWrapper>
          )}

          {vehicleDetailsValid && isHistoryApiSuccessful && vin !== null && (
            <history-state-hydrator
              year-id={yearId}
              make-id={makeId}
              model-id={modelId}
              trim-id={trimId}
              color-id={color}
              transmission-id={transmission}
              engine-id={engine}
              drivetrain-id={drivetrain}
              vin={vin}
              mileage={mileage}
              allow-auto-answer={true}
              show-form-validation={true}
              allow-ineligible-vehicle-submission={allowIneligibleVehiclesForWebComponent}
              align-left={isOfferCreationComponentEnabled}>
              <vehicle-history />
            </history-state-hydrator>
          )}
        </WebComponentContainer>
      )}
    </div>
  )
}

const KeysModWrapper = styled.div`
  box-shadow: 0 0 #0000, 0 0 #0000, 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1);
  padding: 0px 20px 20px 20px;
  margin-bottom: 36px;
  @media (min-width: ${breakpoints.highmed}px) {
    padding: 20px 68px 20px 68px;
  }
`

export default VehicleDetailsWebComponent
